import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTheme } from '../../theme/useTheme';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
  TextInput,
  FlatList,
  ActivityIndicator,
  useWindowDimensions,
} from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useTranslation } from 'react-i18next';
import FontFamily from '../../constants/fonts';
import { AdminColors } from '../Admin/constants/adminColors';
import LhDatePicker from '../../components/LhDatePicker';
import HarborDataTable from '../harbor/HarborDataTable';
import PerfectTruckRecordCard from './PerfectTruckRecordCard';
import { saveFilters, setLastLocationId, clearFilters } from '../../store/slices/perfectTruckDashboardFiltersSlice';
import PerfectTruckFab from './PerfectTruckFab';
import { DeliveryListItem, DeliveryStatus, DashboardKpi, RouteTypeItem } from './types';
import { fetchDashboardKpis, fetchDeliveries, searchDeliveries, fetchActiveRouteTypes } from './perfectTruckApi';
import { formatDate, formatUserName, formatTimeAmPm, resolveRouteTypeLabel } from './perfectTruckUtils';
import { hasPerfectTruckFullAccess } from '../harbor/harborRoles';
import type { RootState } from '../../store';
import { useDarkStyles, useDarkColor } from '../../theme/useDarkStyles';

interface FeedbackCategoryItem {
  id: string;
  displayText: string;
}

interface FeedbackCategoryItem {
  id: string;
  displayText: string;
}

const MOBILE_PAGE_SIZE = 20;

// Feedback category options.
// `apiValue` is the untranslated value persisted/sent to the API and compared
// against record.feedbackCategory — it must never be localized. Only the label
// shown to the user (`tKey`) is translated.
const FEEDBACK_CATEGORY_DEFS: { apiValue: string; tKey: string }[] = [
  { apiValue: 'Duplicate Photos', tKey: 'feedback_categories.duplicate_photos' },
  { apiValue: 'Equipment Marked as Loaded, is not Loaded', tKey: 'feedback_categories.equipment_not_loaded' },
  { apiValue: 'No Perfect Truck Details for My Route', tKey: 'feedback_categories.no_pt_details' },
  { apiValue: 'Other', tKey: 'feedback_categories.other' },
];

type FilterType = 'all' | 'open' | 'done';

interface DashboardFilters {
  filter: FilterType;
  locationName?: string;
  deliveryDate?: string;
  feedbackPresent?: boolean;
  feedbackCategory?: string[];
}

// Colors only — the visible label is resolved via t(`status.${status}`) in StatusBadge.
const STATUS_CONFIG: Record<DeliveryStatus, { bg: string; color: string }> = {
  'Pending Check': { bg: AdminColors.yellow50, color: AdminColors.yellow200 },
  'In Progress': { bg: AdminColors.blue50, color: AdminColors.blue700 },
  'Approved': { bg: AdminColors.green75, color: AdminColors.green600 },
  'Rejected': { bg: AdminColors.red75, color: AdminColors.red400 },
  'In Progress - Certifier': { bg: AdminColors.blue50, color: AdminColors.blue700 },
  'In Progress - Checker': { bg: AdminColors.blue50, color: AdminColors.blue700 },
  'Approved - No Certification': { bg: AdminColors.green75, color: AdminColors.green600 },
  'Rejected - No Certification': { bg: AdminColors.red75, color: AdminColors.red400 },
};



// Dark-theme variants: solid dark tinted backgrounds with white text so the label
// stays readable (global dark mode forces text to white).
const STATUS_CONFIG_DARK: Record<DeliveryStatus, { bg: string; color: string }> = {
  'Pending Check': { bg: '#92600A', color: '#FFFFFF' },
  'In Progress': { bg: '#1A5BB5', color: '#FFFFFF' },
  'Approved': { bg: '#1A6B24', color: '#FFFFFF' },
  'Rejected': { bg: '#9B2626', color: '#FFFFFF' },
  'In Progress - Certifier': { bg: '#1A5BB5', color: '#FFFFFF' },
  'In Progress - Checker': { bg: '#1A5BB5', color: '#FFFFFF' },
  'Approved - No Certification': { bg: '#1A6B24', color: '#FFFFFF' },
  'Rejected - No Certification': { bg: '#9B2626', color: '#FFFFFF' },
};

// Dark-theme KPI icon tile backgrounds keyed by the light-theme icon bg.
const KPI_ICON_BG_DARK: Record<string, string> = {
  [AdminColors.blue50]: '#1A5BB5',
  [AdminColors.yellow50]: '#92600A',
  [AdminColors.green75]: '#1A6B24',
};

const StatusBadge: React.FC<{ status: DeliveryStatus }> = ({ status }) => {
  const styles = useDarkStyles(baseStyles);
  const { t } = useTranslation('perfectTruck');
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const cfg = (isDark ? STATUS_CONFIG_DARK : STATUS_CONFIG)[status]
    ?? (isDark ? { bg: '#2B2B2B', color: '#FFFFFF' } : { bg: AdminColors.gray50, color: AdminColors.gray450 });
  return (
    <View style={[styles.badge, { backgroundColor: cfg.bg }]}>
      <Text style={[styles.badgeText, { color: cfg.color }]}>{t(`status.${status}`, { defaultValue: status })}</Text>
    </View>
  );
};

// ── Route Type Toggle Switch Component ──
interface RouteTypeToggleProps {
  selectedTab: string;
  onTabChange: (routeType: string) => void;
}

const RouteTypeToggle: React.FC<RouteTypeToggleProps> = ({ 
  selectedTab, 
  onTabChange 
}) => {
  const styles = useDarkStyles(baseStyles);
  const { t } = useTranslation('perfectTruck');
  const routeTypes = ['DSD', 'WHD'];

  return (
    <View style={styles.toggleContainer}>
      {routeTypes.map((routeType) => (
        <TouchableOpacity
          key={routeType}
          style={[
            styles.toggleTab,
            selectedTab === routeType && styles.toggleTabActive,
          ]}
          onPress={() => onTabChange(routeType)}
          activeOpacity={0.8}
        >
          <Text
            style={[
              styles.toggleText,
              selectedTab === routeType && styles.toggleTextActive,
            ]}
          >
            {t(`route_types.${routeType.toLowerCase()}`)}
          </Text>
        </TouchableOpacity>
      ))}
    </View>
  );
};

interface KPICardProps {
  value: number;
  label: string;
  color: string;
  bgColor: string;
  iconName: string;
  accentColor: string;
}

const KPICard: React.FC<KPICardProps> = ({ value, label, color, bgColor, iconName, accentColor }) => {
  const styles = useDarkStyles(baseStyles);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const iconBg = isDark ? (KPI_ICON_BG_DARK[bgColor] ?? bgColor) : bgColor;
  return (
    <View style={styles.kpiCard}>
      <View style={[styles.kpiAccent, { backgroundColor: accentColor }]} />
      <View style={[styles.kpiIcon, { backgroundColor: iconBg }]}>
        <MaterialIcons name={iconName} size={22} color={isDark ? '#FFFFFF' : color} />
      </View>
      <View style={styles.kpiContent}>
        <Text style={[styles.kpiValue, { color }]}>{value}</Text>
        <Text style={styles.kpiLabel}>{label}</Text>
      </View>
    </View>
  );
};

interface PerfectTruckDashboardProps {
  onRowPress: (record: DeliveryListItem) => void;
  onCreatePress?: () => void;
  canCreate?: boolean;
  onViewPress?: (record: DeliveryListItem) => void;
}

const PerfectTruckDashboard: React.FC<PerfectTruckDashboardProps> = ({ onRowPress, onCreatePress, canCreate, onViewPress }) => {
  const dc = useDarkColor();
  const styles = useDarkStyles(baseStyles);
  const { t, i18n } = useTranslation('perfectTruck');
  const currentLanguage = (i18n.language ?? 'en').split('-')[0];
  const roleName = useSelector((state: RootState) => state.appData?.userInfo?.roleName);
  const selectedLocation = useSelector((state: RootState) => state.appData?.selectedLocation);
  const hasPTFullAccess = hasPerfectTruckFullAccess(roleName);
  const isReadOnlyAccess = !hasPTFullAccess;
  const canShowCreate = Boolean(canCreate && hasPTFullAccess);
  const [filters, setFilters] = useState<DashboardFilters>({
    filter: 'all',
    locationName: selectedLocation?.name,
    deliveryDate: undefined,
    feedbackPresent: undefined,
    feedbackCategory: undefined,
  });
  
  const [searchQuery, setSearchQuery] = useState('');
  const [kpis, setKpis] = useState<DashboardKpi | null>(null);
  const [records, setRecords] = useState<DeliveryListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [selectedRouteType, setSelectedRouteType] = useState('DSD');
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>({
    feedbackCategory: [],
  });
  const [routeTypes, setRouteTypes] = useState<RouteTypeItem[]>([]);
  const isDarkTheme = useTheme().resolvedTheme === 'dark';
  const windowWidth = useWindowDimensions().width;
  const isLoadingMoreRef = React.useRef(false);
  const lastRequestKeyRef = React.useRef<string>('');
  const isMobile = Platform.OS !== 'web' || windowWidth < 1024;

  const toYyyyMmDd = useCallback((date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, []);

  const selectedDeliveryDate = useMemo(() => {
    if (!filters.deliveryDate) return null;
    const [y, m, d] = filters.deliveryDate.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  }, [filters.deliveryDate]);

  const updateFilters = useCallback((updates: Partial<DashboardFilters>) => {
    setFilters((prev) => {
      const next = { ...prev, ...updates };
      if (
        prev.filter === next.filter &&
        prev.locationName === next.locationName &&
        prev.deliveryDate === next.deliveryDate &&
        prev.feedbackPresent === next.feedbackPresent &&
        JSON.stringify(prev.feedbackCategory) === JSON.stringify(next.feedbackCategory)
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const applyDeliveryDateFilter = useCallback((date: Date | null) => {
    if (date) {
      updateFilters({
        deliveryDate: toYyyyMmDd(date),
        filter: 'all',
      });
      return;
    }
    updateFilters({ deliveryDate: undefined });
  }, [toYyyyMmDd, updateFilters]);

  const buildRequestKey = useCallback(
    (
      nextFilters: DashboardFilters,
      page: number,
      size: number,
      sort?: string,
      searchTerm?: string,
      deliveryType?: string,
    ) => [
      nextFilters.filter,
      nextFilters.locationName ?? '',
      nextFilters.deliveryDate ?? '',
      nextFilters.feedbackPresent ?? '',
      JSON.stringify(nextFilters.feedbackCategory ?? []),
      page,
      size,
      sort ?? '',
      searchTerm ?? '',
      deliveryType ?? '',
    ].join('|'),
    [],
  );

  useEffect(() => {
    const nextLocation = selectedLocation?.name;
    updateFilters({ locationName: nextLocation });
  }, [selectedLocation?.name, updateFilters]);

  // Sync main filter's feedbackCategory with table's columnFilters
  useEffect(() => {
    setColumnFilters((prev) => ({
      ...prev,
      feedbackCategory: filters.feedbackCategory ?? [],
    }));
  }, [filters.feedbackCategory]);

  const handleRowPress = useCallback((record: DeliveryListItem) => {
    if (isReadOnlyAccess && record.status === 'Pending Check' && onViewPress) {
      onViewPress(record);
      return;
    }
    onRowPress(record);
  }, [isReadOnlyAccess, onRowPress, onViewPress]);

  const loadDataWithFilter = useCallback(
    async (nextFilters: DashboardFilters, page: number, size: number, sort?: string) => {
      const requestKey = buildRequestKey(nextFilters, page, size, sort, undefined, selectedRouteType);
      if (lastRequestKeyRef.current === requestKey) {
        return;
      }
      try {
        setIsLoading(true);
        setApiError('');
        const locationName = nextFilters.locationName;
        const [kpiData, listData] = await Promise.all([
          fetchDashboardKpis(locationName, nextFilters.filter, selectedRouteType),
          fetchDeliveries(
            nextFilters.filter,
            locationName,
            page,
            size,
            sort,
            nextFilters.deliveryDate,
            nextFilters.feedbackPresent,
            nextFilters.feedbackCategory,
            selectedRouteType
          ),
        ]);
        lastRequestKeyRef.current = requestKey;
        setKpis(kpiData);
        setRecords(listData.data);
        setCurrentPage(listData.currentPage);
        setTotalPages(listData.totalPages);
      } catch (e: any) {
        console.error('Failed to load Perfect Truck dashboard data:', e);
        setApiError(t('dashboard.error_failed_load'));
      } finally {
        setIsLoading(false);
      }
    },
    [selectedLocation, t, buildRequestKey, selectedRouteType]
  );

  const handleSearch = useCallback(
    async (term: string, page: number = 0, nextFilters: DashboardFilters = filters) => {
      if (!nextFilters.locationName) {
        setApiError(t('dashboard.error_no_location'));
        return;
      }

      const searchTerm = term.trim();
      const requestKey = buildRequestKey(nextFilters, page, pageSize, undefined, searchTerm, selectedRouteType);
      if (lastRequestKeyRef.current === requestKey) {
        return;
      }

      try {
        setIsLoading(true);
        setApiError('');
        const [kpiData, result] = await Promise.all([
          fetchDashboardKpis(nextFilters.locationName, nextFilters.filter, selectedRouteType),
          searchDeliveries(
            searchTerm,
            nextFilters.locationName,
            nextFilters.filter,
            nextFilters.deliveryDate,
            page,
            pageSize,
            nextFilters.feedbackPresent,
            nextFilters.feedbackCategory,
            sortBy,
            selectedRouteType,
          ),
        ]);
        lastRequestKeyRef.current = requestKey;
        setKpis(kpiData);
        setRecords(result.data);
        setCurrentPage(result.currentPage);
        setTotalPages(result.totalPages);
      } catch (e: any) {
        console.error('Perfect Truck search failed:', e);
        setApiError(t('dashboard.error_search_failed'));
        setRecords([]);
      } finally {
        setIsLoading(false);
      }
    },
    [filters, buildRequestKey, pageSize, sortBy, t, selectedRouteType]
  );

  // Load data when filters or table controls change (always reset to page 0)
  // Keep search + filters in sync by rebuilding from the latest unified filter state.
  useEffect(() => {
    const searchTerm = searchQuery.trim();
    setCurrentPage(0);
    if (searchTerm) {
      handleSearch(searchTerm, 0, filters);
      return;
    }
    loadDataWithFilter(filters, 0, pageSize, sortBy);
  }, [filters, handleSearch, loadDataWithFilter, pageSize, sortBy]);

  // Load data when page changes (skip first render — covered by the effect above)
  // Mobile uses infinite scroll (loadMoreMobile appends), so this effect must be web-only.
  // On mobile, currentPage changes driven by loadMoreMobile would otherwise trigger a
  // full-replace reload here, causing an infinite fetch loop.
  const skipInitialPageLoad = React.useRef(true);
  useEffect(() => {
    if (isMobile) return;
    if (skipInitialPageLoad.current) {
      skipInitialPageLoad.current = false;
      return;
    }
    const searchTerm = searchQuery.trim();
    if (searchTerm) {
      handleSearch(searchTerm, currentPage, filters);
      return;
    }
    loadDataWithFilter(filters, currentPage, pageSize, sortBy);
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle search query changes with debouncing (mobile and web)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        setCurrentPage(0);
        handleSearch(searchQuery.trim(), 0, filters);
      } else if (searchQuery.trim().length === 0) {
        // Only reload when fully cleared — ignore 1-char transient input
        setCurrentPage(0);
        loadDataWithFilter(filters, 0, pageSize, sortBy);
      }
    }, 500); // Debounce 500ms

    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch, loadDataWithFilter, filters, pageSize, sortBy]);

  

  const exportTable = useCallback(async () => {
    if (isMobile) return;
    setIsExporting(true);
    try {
      // Use filteredRecords which is already filtered by route type and status
      const dataToExport = filteredRecords;

      // Localize feedback category helper
      const localizeFeedbackCat = (value?: string | null) => {
        if (!value) return '—';
        const def = FEEDBACK_CATEGORY_DEFS.find((d) => d.apiValue === value);
        return def ? t(def.tKey, { defaultValue: def.apiValue }) : value;
      };

      // Create Excel workbook with route-type-specific sheet name
      const ExcelJSModule = await import('exceljs');
      const ExcelJS = ExcelJSModule.default || ExcelJSModule;
      const workbook = new ExcelJS.Workbook();
      const translatedRouteType = t(`route_types.${selectedRouteType.toLowerCase()}`, selectedRouteType);
      const worksheet = workbook.addWorksheet(`${translatedRouteType} Export`);

      // Get the appropriate columns for the selected route type
      const tableColumns = selectedRouteType === 'WHD' ? getWHDColumns() : getDSDColumns();

      // Add header row using TABLE_COLUMNS
      const headerRow = worksheet.addRow(tableColumns.map((col) => col.header));

      // Style header row
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0A5EB0' } };
      headerRow.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      headerRow.height = 20;

      // Add data rows
      dataToExport.forEach((record) => {
        // Translate status value for export
        const translatedStatus = t(`status.${record.status}`, { defaultValue: record.status });
        const row = worksheet.addRow([
          record.routeId,
          resolveRouteTypeLabel(record.routeType, routeTypes),
          record.loadSeq ?? '—',
          record.assetId,
          formatDate(record.deliveryDate),
          translatedStatus,
          formatUserName(record.certifierName),
          formatTimeAmPm(record.certifiedAt),
          formatUserName(record.checkerName),
          formatTimeAmPm(record.checkedAt),
          localizeFeedbackCat(record.feedbackCategory),
          record.checkerComment ?? '',
        ]);

        row.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
        row.font = { size: 11 };
        // Alternate row colors
        if (row.number % 2 === 0) {
          row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
        }
      });

      // Set column widths
      worksheet.columns = tableColumns.map(() => ({ width: 16 }));

      // Freeze the header row
      worksheet.views = [{ state: 'frozen', ySplit: 1 }];

      // Generate and download file
      if (typeof document !== 'undefined') {
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `perfect_truck_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
    } catch (e: any) {
      setApiError(t('dashboard.error_failed_export'));
    } finally {
      setIsExporting(false);
    }
  }, [records, filters, selectedRouteType, t, isMobile, routeTypes]);

  // Mobile: infinite scroll — append next page on end-reached
  const loadMoreMobile = useCallback(async () => {
    if (!isMobile || isLoading || isLoadingMoreRef.current) return;
    if (currentPage + 1 >= totalPages) return;
    // Set ref synchronously before any async work so concurrent calls are blocked
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const listData = await fetchDeliveries(
        filters.filter,
        filters.locationName,
        nextPage,
        MOBILE_PAGE_SIZE,
        sortBy,
        filters.deliveryDate,
        filters.feedbackPresent,
        filters.feedbackCategory,
      );
      setRecords((prev) => [...prev, ...listData.data]);
      setCurrentPage(listData.currentPage);
      setTotalPages(listData.totalPages);
    } catch (e: any) {
      console.error('Failed to load more Perfect Truck records:', e);
      setApiError(t('dashboard.error_failed_more'));
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, [isMobile, isLoading, currentPage, totalPages, filters, sortBy, t]);

  // Mobile uses a smaller page size — keep web behavior unchanged
  useEffect(() => {
    if (isMobile && pageSize !== MOBILE_PAGE_SIZE) {
      setPageSize(MOBILE_PAGE_SIZE);
    }
  }, [isMobile, pageSize]);

  // Route type is persisted as a language-independent ID; fetch the config once to
  // resolve it back to a display label (legacy records store the label directly).
  useEffect(() => {
    fetchActiveRouteTypes(currentLanguage)
      .then(setRouteTypes)
      .catch(() => setRouteTypes([]));
  }, [currentLanguage]);

  const filteredRecords = records.filter((r) => {
    // Apply status filter (all/open/done)
    const statusMatch =
      filters.filter === 'all' ||
      (filters.filter === 'open' && (
        r.status === 'Pending Check' ||
        r.status === 'In Progress' ||
        r.status === 'In Progress - Certifier' ||
        r.status === 'In Progress - Checker'
      )) ||
      (filters.filter === 'done' && (r.status === 'Approved' || r.status === 'Rejected'));

    // Apply delivery type filter (DSD/WHD)
    const deliveryTypeMatch = r.deliveryType === selectedRouteType;

    return statusMatch && deliveryTypeMatch;
  });

  // Compute KPI values from filteredRecords so they always reflect the active filter + search
  const kpiNextDay = kpis?.nextDeliveryDayCount ?? 0;
  const kpiCurrentDay = kpis?.currentDeliveryDayCount ?? 0;
  const kpiClosed = kpis?.closedOutTodayCount ?? 0;

  // Dropdown items: `id` stays the untranslated API value so filtering keeps working
  // across language switches, while `displayText` is localized for the UI.
  const feedbackCategories: FeedbackCategoryItem[] = useMemo(
    () => FEEDBACK_CATEGORY_DEFS.map((def) => ({
      id: def.apiValue,
      displayText: t(def.tKey, { defaultValue: def.apiValue }),
    })),
    [t],
  );

  // Localize a stored feedback category value for display in the table cell.
  const localizeFeedbackCategory = useCallback(
    (value?: string | null) => {
      if (!value) return '—';
      const def = FEEDBACK_CATEGORY_DEFS.find((d) => d.apiValue === value);
      return def ? t(def.tKey, { defaultValue: def.apiValue }) : value;
    },
    [t],
  );

  // ── Table Columns Configuration ───────────────────────────────────────
  const getDSDColumns = useCallback(() => [
    {
      columnKey: 'routeId',
      header: t('dashboard.col_route'),
      renderCell: (item: any) => (
        <TouchableOpacity
          onPress={() => handleRowPress(item as DeliveryListItem)}
          activeOpacity={1}
        >
          <Text style={styles.routeLink}>{item.routeId}</Text>
        </TouchableOpacity>
      ),
    },
    { columnKey: 'routeType', header: t('dashboard.col_type'), renderCell: (item: any) => {
        const routeTypeLabel = resolveRouteTypeLabel(item.routeType, routeTypes);
        const translatedLabel = t(`route_types.${routeTypeLabel.toLowerCase()}`, routeTypeLabel);
        return <Text style={styles.defaultCellText}>{translatedLabel}</Text>;
      }
    },
    {
      columnKey: 'loadSeq',
      header: t('dashboard.col_load_seq'),
      renderCell: (item: any) => <Text style={styles.cellText}>{item.loadSeq ?? '--'}</Text>,
    },
    { columnKey: 'assetId', header: t('dashboard.col_asset') },
    { columnKey: 'deliveryDate', header: t('dashboard.col_delivery_date'), renderCell: (item: any) => <Text style={styles.defaultCellText}>{formatDate(item.deliveryDate)}</Text> },
    {
      columnKey: 'status',
      header: t('dashboard.col_status'),
      width: 220,
      renderCell: (item: any) => (
        <View style={[styles.statusCellContainer, { width: '100%'}]}>
          <StatusBadge status={item.status} />
          {item.status === 'Pending Check' && onViewPress ? (
            <TouchableOpacity
              style={styles.eyeButton}
              onPress={() => onViewPress(item as DeliveryListItem)}
              accessibilityLabel={t('dashboard.aria_view_certification')}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <MaterialIcons name="visibility" size={17} color={dc(AdminColors.blue700)} />
            </TouchableOpacity>
          ) : null}
        </View>
      ),
    },
    {
      columnKey: 'certifierName',
      header: t('dashboard.col_certifier'),
      formatter: (v: any) => formatUserName(v),
      renderCell: (item: any) => <Text style={styles.defaultCellText}>{formatUserName(item.certifierName)}</Text>,
    },
    {
      columnKey: 'certifiedAt',
      header: t('dashboard.col_certified_at'),
      renderCell: (item: any) => <Text style={styles.defaultCellText}>{formatTimeAmPm(item.certifiedAt)}</Text>,
    },
    {
      columnKey: 'checkerName',
      header: t('dashboard.col_checker'),
      formatter: (v: any) => formatUserName(v),
      renderCell: (item: any) => <Text style={styles.defaultCellText}>{formatUserName(item.checkerName)}</Text>,
    },
    {
      columnKey: 'checkedAt',
      header: t('dashboard.col_checked_at'),
      renderCell: (item: any) => <Text style={styles.defaultCellText}>{formatTimeAmPm(item.checkedAt)}</Text>,
    },
    {
      columnKey: 'feedbackCategory',
      header: t('dashboard.col_feedback_category'),
      renderCell: (item: any) => (
        <Text style={styles.defaultCellText}>{localizeFeedbackCategory(item.feedbackCategory)}</Text>
      ),
      // `value` must stay the untranslated API value the backend filters on.
      filterOptions: feedbackCategories.map((cat) => ({ label: cat.displayText, value: cat.id })),
    },
    {
      columnKey: 'checkerFeedback',
      header: t('dashboard.col_checker_feedback'),
      sortable: false,
      renderCell: (item: any) => (
        item.checkerComment ? (
          <View style={styles.feedbackIndicatorCell}>
            <MaterialIcons name="comment" size={18} color={dc(AdminColors.blue700)} />
            <Text style={styles.feedbackIndicatorText}>{t('dashboard.feedback_notes')}</Text>
          </View>
        ) : (
          <Text style={styles.defaultCellText}>—</Text>
        )
      ),
    },
  ], [handleRowPress, onViewPress, t, feedbackCategories, localizeFeedbackCategory, routeTypes]);

  const getWHDColumns = useCallback(() => [
    {
      columnKey: 'routeId',
      header: t('dashboard.col_bol_number', 'BOL#'),
      renderCell: (item: any) => (
        <TouchableOpacity
          onPress={() => handleRowPress(item as DeliveryListItem)}
          activeOpacity={1}
        >
          <Text style={styles.routeLink}>{item.routeId}</Text>
        </TouchableOpacity>
      ),
    },
    { columnKey: 'routeType', header: t('dashboard.col_type'), renderCell: (item: any) => {
        const routeTypeLabel = resolveRouteTypeLabel(item.routeType, routeTypes);
        const translatedLabel = t(`route_types.${routeTypeLabel.toLowerCase()}`, routeTypeLabel);
        return <Text style={styles.defaultCellText}>{translatedLabel}</Text>;
      }
    },
    { columnKey: 'assetId', header: t('dashboard.col_asset') },
    { 
      columnKey: 'deliveryDate', 
      header: t('dashboard.col_expected_delivery_date', 'Expected Delivery Date'), 
      renderCell: (item: any) => <Text style={styles.defaultCellText}>{formatDate(item.deliveryDate)}</Text> 
    },
    {
      columnKey: 'status',
      header: t('dashboard.col_status'),
      renderCell: (item: any) => (
        <View style={styles.statusCellContainer}>
          <StatusBadge status={item.status} />
          {item.status === 'Pending Check' && onViewPress ? (
            <TouchableOpacity
              style={styles.eyeButton}
              onPress={() => onViewPress(item as DeliveryListItem)}
              accessibilityLabel={t('dashboard.aria_view_certification')}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <MaterialIcons name="visibility" size={17} color={dc(AdminColors.blue700)} />
            </TouchableOpacity>
          ) : null}
        </View>
      ),
    },
    {
      columnKey: 'certifierName',
      header: t('dashboard.col_certifier'),
      formatter: (v: any) => formatUserName(v),
      renderCell: (item: any) => <Text style={styles.defaultCellText}>{formatUserName(item.certifierName)}</Text>,
    },
    {
      columnKey: 'certifiedAt',
      header: t('dashboard.col_certification_time', 'Certification Time'),
      renderCell: (item: any) => <Text style={styles.defaultCellText}>{formatTimeAmPm(item.certifiedAt)}</Text>,
    },
    {
      columnKey: 'checkerName',
      header: t('dashboard.col_checker'),
      formatter: (v: any) => formatUserName(v),
      renderCell: (item: any) => <Text style={styles.defaultCellText}>{formatUserName(item.checkerName)}</Text>,
    },
    {
      columnKey: 'checkedAt',
      header: t('dashboard.col_check_time', 'Check Time'),
      renderCell: (item: any) => <Text style={styles.defaultCellText}>{formatTimeAmPm(item.checkedAt)}</Text>,
    },
  ], [handleRowPress, onViewPress, t, routeTypes]);

  const TABLE_COLUMNS = useMemo(() => 
    selectedRouteType === 'WHD' ? getWHDColumns() : getDSDColumns()
  , [selectedRouteType, getDSDColumns, getWHDColumns]);

  const FILTERS: { key: FilterType; label: string }[] = [
    { key: 'all', label: t('dashboard.filter_all') },
    { key: 'open', label: t('dashboard.filter_open') },
    { key: 'done', label: t('dashboard.filter_done') },
  ];

  // ── Mobile render ─────────────────────────────────────────────────────────
  if (isMobile) {
    const ListHeader = (
      <View>
        {isReadOnlyAccess ? (
          <View style={styles.readOnlyNote}>
            <MaterialIcons name="visibility" size={18} color={dc(AdminColors.blue700)} />
            <Text style={styles.readOnlyNoteText}>{t('dashboard.readonly_banner')}</Text>
          </View>
        ) : null}

        {apiError ? (
          <View style={styles.errorBanner}>
            <MaterialIcons name="error-outline" size={16} color={dc(AdminColors.red400)} />
            <Text style={styles.errorText}>{apiError}</Text>
          </View>
        ) : null}

        {/* KPI row — 3-up horizontal grid, accent strip on top, no icon */}
        <View style={styles.kpiRowMobile}>
          <KPITile value={kpiNextDay} label={t('dashboard.kpi_next_day')} color={AdminColors.blue700} accentColor={AdminColors.blue700} />
          <KPITile value={kpiCurrentDay} label={t('dashboard.kpi_today')} color={AdminColors.yellow200} accentColor={AdminColors.yellow200} />
          <KPITile value={kpiClosed} label={t('dashboard.kpi_closed_today')} color={AdminColors.green600} accentColor={AdminColors.green600} />
        </View>

        {/* Route Type Toggle */}
        <RouteTypeToggle 
          selectedTab={selectedRouteType} 
          onTabChange={setSelectedRouteType}
        />

        {/* Mobile search bar */}
        <TextInput
          style={styles.searchBoxMobile}
          placeholder={t('dashboard.search_placeholder')}
          placeholderTextColor={dc(AdminColors.textMuted)}
          value={searchQuery}
          onChangeText={setSearchQuery}
        />

        {/* Section header + filter pills */}
        <View style={styles.mobileListHeader}>
          <Text style={styles.mobileListTitle}>{t('dashboard.recent_records')}</Text>
        </View>
        <View style={styles.mobileFilterRow}>
          <View style={styles.pills}>
            {FILTERS.map((f) => (
              <TouchableOpacity
                key={f.key}
                style={[styles.pill, filters.filter === f.key && styles.pillActive]}
                onPress={() => updateFilters({ filter: f.key })}
              >
                <Text style={[styles.pillText, filters.filter === f.key && styles.pillTextActive]}>
                  {f.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
          <View style={styles.dateFilterGroup}>
            <View style={styles.datePickerContainerMobile}>
              <LhDatePicker
                value={selectedDeliveryDate}
                onChange={applyDeliveryDateFilter}
                placeholder={t('dashboard.placeholder_delivery_date')}
                colorScheme={isDarkTheme ? 'dark' : 'light'}
                iconColor={isDarkTheme ? '#FFFFFF' : '#3A3A3A'}
              />
            </View>
            {filters.deliveryDate ? (
              <TouchableOpacity
                style={styles.dateClearButton}
                onPress={() => updateFilters({ deliveryDate: undefined })}
              >
                <Text style={styles.dateClearText}>{t('dashboard.button_clear')}</Text>
              </TouchableOpacity>
            ) : null}
          </View>
          <View style={styles.mobileFeedbackFilterRow}>
            <TouchableOpacity
              style={[styles.pill, filters.feedbackPresent && styles.pillActive]}
              onPress={() => {
                setCurrentPage(0);
                updateFilters({ feedbackPresent: !filters.feedbackPresent });
              }}
            >
              <Text style={[styles.pillText, filters.feedbackPresent && styles.pillTextActive]}>
                {t('dashboard.filter_has_driver_feedback')}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );

    const ListFooter = isLoadingMore ? (
      <View style={styles.footerLoader}>
        <ActivityIndicator size="small" color={dc(AdminColors.blue700)} />
      </View>
    ) : null;

    const EmptyState = !isLoading ? (
      <View style={styles.emptyState}>
        <MaterialIcons name="inbox" size={40} color={dc(AdminColors.gray150)} />
        <Text style={styles.emptyText}>{t('dashboard.no_records')}</Text>
      </View>
    ) : null;

    return (
      <View style={styles.mobileRoot}>
        <FlatList
          style={styles.container}
          contentContainerStyle={styles.contentContainerMobile}
          data={filteredRecords}
          keyExtractor={(item) => String(item.id)}
          renderItem={({ item }) => {
            const routeTypeLabel = resolveRouteTypeLabel(item.routeType, routeTypes);
            const translatedRouteType = t(`route_types.${routeTypeLabel.toLowerCase()}`, routeTypeLabel);
            return <PerfectTruckRecordCard record={{ ...item, routeType: translatedRouteType }} onPress={handleRowPress} onViewPress={onViewPress} />;
          }}
          ListHeaderComponent={ListHeader}
          ListFooterComponent={ListFooter}
          ListEmptyComponent={isLoading ? (
            <View style={styles.footerLoader}>
              <ActivityIndicator size="small" color={dc(AdminColors.blue700)} />
            </View>
          ) : EmptyState}
          onEndReached={loadMoreMobile}
          onEndReachedThreshold={0.4}
        />
        {canShowCreate && onCreatePress ? (
          <PerfectTruckFab onPress={onCreatePress} accessibilityLabel={t('dashboard.aria_new_certification')} />
        ) : null}
      </View>
    );
  }

  // ── Web / desktop render (unchanged) ──────────────────────────────────────
  return (
    <View style={styles.webRoot}>
      <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      {isReadOnlyAccess ? (
        <View style={styles.readOnlyNote}>
          <MaterialIcons name="visibility" size={18} color={dc(AdminColors.blue700)} />
          <Text style={styles.readOnlyNoteText}>{t('dashboard.readonly_banner')}</Text>
        </View>
      ) : null}

      {apiError ? (
        <View style={styles.errorBanner}>
          <MaterialIcons name="error-outline" size={16} color={dc(AdminColors.red400)} />
          <Text style={styles.errorText}>{apiError}</Text>
        </View>
      ) : null}

      {/* KPI row */}
      <View style={styles.kpiRow}>
        <KPICard
          value={kpiNextDay}
          label={t('dashboard.kpi_next_delivery_day')}
          color={AdminColors.blue700}
          bgColor={AdminColors.blue50}
          iconName="event"
          accentColor={AdminColors.blue700}
        />
        <KPICard
          value={kpiCurrentDay}
          label={t('dashboard.kpi_current_delivery_day')}
          color={AdminColors.yellow200}
          bgColor={AdminColors.yellow50}
          iconName="list-alt"
          accentColor={AdminColors.yellow200}
        />
        <KPICard
          value={kpiClosed}
          label={t('dashboard.kpi_closed_today_long')}
          color={AdminColors.green600}
          bgColor={AdminColors.green75}
          iconName="check-circle"
          accentColor={AdminColors.green600}
        />
      </View>

      {/* Route Type Toggle */}
      <RouteTypeToggle 
        selectedTab={selectedRouteType} 
        onTabChange={setSelectedRouteType}
      />

      {/* Controls row */}
      <View style={styles.controlsRow}>
        <View style={styles.controlsLeft}>
          <View style={styles.pills}>
            {FILTERS.map((f) => (
              <TouchableOpacity
                key={f.key}
                style={[styles.pill, filters.filter === f.key && styles.pillActive]}
                onPress={() => updateFilters({ filter: f.key })}
              >
                <Text style={[styles.pillText, filters.filter === f.key && styles.pillTextActive]}>
                  {f.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
          <View style={styles.dateFilterGroup}>
            <View style={styles.datePickerContainer}>
              <LhDatePicker
                value={selectedDeliveryDate}
                onChange={applyDeliveryDateFilter}
                placeholder={t('dashboard.placeholder_delivery_date')}
                colorScheme={isDarkTheme ? 'dark' : 'light'}
                iconColor={isDarkTheme ? '#FFFFFF' : '#3A3A3A'}
              />
            </View>
            {filters.deliveryDate ? (
              <TouchableOpacity
                style={styles.dateClearButton}
                onPress={() => updateFilters({ deliveryDate: undefined })}
              >
                <Text style={styles.dateClearText}>{t('dashboard.button_clear')}</Text>
              </TouchableOpacity>
            ) : null}
          </View>
          <View style={styles.feedbackFilterGroup}>
            <TouchableOpacity
              style={[styles.pill, filters.feedbackPresent && styles.pillActive]}
              onPress={() => {
                setCurrentPage(0);
                updateFilters({ feedbackPresent: !filters.feedbackPresent });
              }}
            >
              <Text style={[styles.pillText, filters.feedbackPresent && styles.pillTextActive]}>
                {t('dashboard.filter_has_driver_feedback')}
              </Text>
            </TouchableOpacity>
          
          </View>
        </View>
        <View style={styles.searchWrapper}>
          <TextInput
            style={styles.searchBox}
            placeholder={t('dashboard.search_placeholder')}
            placeholderTextColor={dc(AdminColors.textMuted)}
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity style={styles.searchClear} onPress={() => setSearchQuery('')}>
              <MaterialIcons name="close" size={16} color={dc(AdminColors.textMuted)} />
            </TouchableOpacity>
          )}
          {!isMobile && !isReadOnlyAccess && (
            <TouchableOpacity
              style={styles.exportButton}
              onPress={exportTable}
              accessibilityLabel={t('dashboard.button_export_table')}
              disabled={isExporting}
            >
              {isExporting ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text style={styles.exportButtonText}>{t('dashboard.button_export_table') || 'Export Table'}</Text>
              )}
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Table */}
      <View style={styles.tableWrapper}>
        <HarborDataTable
          data={filteredRecords}
          columns={TABLE_COLUMNS}
          tableHeader={t('dashboard.table_title')}
          tableDescription=""
          isLoading={isLoading}
          maxRecordsToShow={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(0);
          }}
          onSort={(key, direction) => {
            const sort = `${key},${direction}`;
            setSortBy(sort);
            setCurrentPage(0);
          }}
          externalPage={currentPage}
          externalTotalPages={totalPages}
          onExternalPageChange={(p) => setCurrentPage(p)}
          onRowClick={(row) => handleRowPress(row as DeliveryListItem)}
          columnFilters={columnFilters}
          locale={{
            rowsPerPageLabel: t('dashboard.pagination.rows_per_page'),
            pageLabel: t('dashboard.pagination.page_label'),
            pageOfLabel: t('dashboard.pagination.page_of_label'),
            firstPageLabel: t('dashboard.pagination.first_page_label'),
            prevPageLabel: t('dashboard.pagination.prev_page_label'),
            nextPageLabel: t('dashboard.pagination.next_page_label'),
            lastPageLabel: t('dashboard.pagination.last_page_label'),
            noDataLabel: t('dashboard.no_records'),
          }}
          onColumnFilter={(columnKey, values) => {
            // Sync table column filter back to main filters
            if (columnKey === 'feedbackCategory') {
              updateFilters({
                feedbackCategory: values.length > 0 ? values : undefined,
              });
            }
            setColumnFilters((prev) => ({ ...prev, [columnKey]: values }));
            setCurrentPage(0);
          }}
        />
      </View>
    </ScrollView>
  </View>
  );
};

// Mobile-only compact KPI tile (no icon, top accent strip)
const KPITile: React.FC<{ value: number; label: string; color: string; accentColor: string }> = ({
  value, label, color, accentColor,
}) => {
  const styles = useDarkStyles(baseStyles);
  const dc = useDarkColor();
  return (
    <View style={styles.kpiTile}>
      <View style={[styles.kpiTileAccent, { backgroundColor: accentColor }]} />
      <Text style={[styles.kpiTileValue, { color: dc(color) }]}>{value}</Text>
      <Text style={styles.kpiTileLabel}>{label}</Text>
    </View>
  );
};

const baseStyles = StyleSheet.create({
  container: { flex: 1, backgroundColor: AdminColors.bgPage },
  mobileRoot: { flex: 1, backgroundColor: AdminColors.bgPage },
  webRoot: { flex: 1, backgroundColor: AdminColors.bgPage },
  contentContainer: { padding: 20 },
  loadingWrapper: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingVertical: 48 }, // kept for error state
  readOnlyNote: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: AdminColors.blue50, borderRadius: 8, padding: 12, marginBottom: 12,
    borderWidth: 1, borderColor: AdminColors.blue200,
  },
  readOnlyNoteText: { fontFamily: FontFamily.medium, fontSize: 13, color: AdminColors.blue700, flex: 1 },
  errorBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: AdminColors.red75, borderRadius: 8, padding: 12, marginBottom: 12,
  },
  errorText: { fontFamily: FontFamily.medium, fontSize: 13, color: AdminColors.red400, flex: 1 },
  kpiRow: { flexDirection: 'row', gap: 14, marginBottom: 20 },
  // Mobile: 3-up horizontal grid with compact tiles
  kpiRowMobile: { flexDirection: 'row', gap: 8, marginBottom: 16 },
  kpiTile: {
    flex: 1, backgroundColor: AdminColors.bgSurface, borderRadius: 10,
    paddingTop: 14, paddingBottom: 12, paddingHorizontal: 8,
    alignItems: 'center', overflow: 'hidden', position: 'relative',
    ...Platform.select({
      web: { boxShadow: '0 1px 3px rgba(0,0,0,0.08)' } as any,
      default: { elevation: 1, shadowColor: '#000', shadowOpacity: 0.08, shadowRadius: 3, shadowOffset: { width: 0, height: 1 } },
    }),
  },
  kpiTileAccent: { position: 'absolute', top: 0, left: 0, right: 0, height: 3 },
  kpiTileValue: { fontFamily: FontFamily.bold, fontSize: 26, lineHeight: 28, marginBottom: 4 },
  kpiTileLabel: {
    fontFamily: FontFamily.bold, fontSize: 9, color: AdminColors.textMuted,
    textAlign: 'center', textTransform: 'uppercase', letterSpacing: 0.3,
  },
  mobileListHeader: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 10, gap: 8,
  },
  mobileFilterRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start',
    flexWrap: 'wrap', gap: 10, marginBottom: 10,
  },
  mobileListTitle: {
    fontFamily: FontFamily.bold, fontSize: 14, color: AdminColors.textPrimary,
  },
  contentContainerMobile: { padding: 14, paddingBottom: 80 },
  footerLoader: { paddingVertical: 16, alignItems: 'center' },
  emptyState: { alignItems: 'center', paddingVertical: 40, gap: 8 },
  emptyText: { fontFamily: FontFamily.regular, fontSize: 13, color: AdminColors.textMuted },
  kpiCard: {
    flex: 1, backgroundColor: AdminColors.bgSurface, borderRadius: 10,
    padding: 18, flexDirection: 'row', alignItems: 'center', gap: 16,
    ...Platform.select({
      web: { boxShadow: '0 0 24px rgba(0,94,192,0.08)' } as any,
      default: { elevation: 2, shadowColor: AdminColors.blue700, shadowOpacity: 0.08, shadowRadius: 12, shadowOffset: { width: 0, height: 0 } },
    }),
    overflow: 'hidden', position: 'relative',
  },
  kpiAccent: { position: 'absolute', left: 0, top: 0, bottom: 0, width: 4 },
  kpiIcon: { width: 44, height: 44, borderRadius: 10, alignItems: 'center', justifyContent: 'center' },
  kpiContent: { flex: 1 },
  kpiValue: { fontFamily: FontFamily.bold, fontSize: 28, lineHeight: 32 },
  kpiLabel: { fontFamily: FontFamily.medium, fontSize: 12, color: AdminColors.textMuted, marginTop: 2 },
  controlsRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 16, gap: 12, flexWrap: 'wrap',
  },
  controlsLeft: {
    flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap',
  },
  pills: { flexDirection: 'row', gap: 6, flexWrap: 'wrap' },
  dateFilterGroup: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  datePickerContainer: { width: 170, minWidth: 160 },
  datePickerContainerMobile: { width: 160, minWidth: 150 },
  dateClearButton: { paddingHorizontal: 8, paddingVertical: 4 },
  dateClearText: { fontFamily: FontFamily.medium, fontSize: 12, color: AdminColors.blue700 },
  pill: {
    paddingHorizontal: 14, paddingVertical: 6, borderRadius: 16,
    borderWidth: 1.5, borderColor: AdminColors.borderDefault, backgroundColor: AdminColors.bgSurface,
  },
  pillActive: { backgroundColor: AdminColors.blue700, borderColor: AdminColors.blue700 },
  pillText: { fontFamily: FontFamily.bold, fontSize: 12, color: AdminColors.textSecondary },
  pillTextActive: { color: AdminColors.textOnDark },
  searchWrapper: {
    flexDirection: 'row', alignItems: 'center',
    position: 'relative', minWidth: 220,
  },
  searchBox: {
    flex: 1, height: 34, borderWidth: 1.5, borderColor: AdminColors.borderDefault, borderRadius: 8,
    paddingLeft: 12, paddingRight: 32, fontFamily: FontFamily.regular, fontSize: 13,
    color: AdminColors.textPrimary, backgroundColor: AdminColors.bgSurface,
  },
  searchClear: {
    position: 'absolute', right: 8, padding: 4,
  },
  searchBoxMobile: {
    height: 38, borderWidth: 1.5, borderColor: AdminColors.borderDefault, borderRadius: 8,
    paddingHorizontal: 12, fontFamily: FontFamily.regular, fontSize: 13,
    color: AdminColors.textPrimary, backgroundColor: AdminColors.bgSurface, marginBottom: 12,
  },
  tableWrapper: {
    backgroundColor: AdminColors.bgSurface, borderRadius: 10, overflow: 'hidden',
    ...Platform.select({
      web: { boxShadow: '0 0 24px rgba(0,94,192,0.08)' } as any,
      default: { elevation: 2 },
    }),
  },
  badgeCell: { flexDirection: 'row', alignItems: 'center' },
  statusCellContainer: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  badge: { paddingHorizontal: 10, paddingVertical: 3, borderRadius: 12 },
  badgeText: { fontFamily: FontFamily.bold, fontSize: 11 },
  routeLink: { fontFamily: FontFamily.bold, fontSize: 13, color: AdminColors.blue700 },
  eyeButton: { padding: 4, alignSelf: 'flex-start', flexShrink: 0 },
  cellText: { fontFamily: FontFamily.medium, fontSize: 13, color: AdminColors.textPrimary },
  defaultCellText: { fontFamily: FontFamily.regular, fontSize: 13, color: '#3A3A3A' },
  metaText: { fontFamily: FontFamily.regular, fontSize: 11, color: AdminColors.textMuted },
  feedbackIndicatorCell: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  feedbackIndicatorText: { fontFamily: FontFamily.medium, fontSize: 12, color: AdminColors.blue700 },
  // Feedback filter styles
  feedbackFilterGroup: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  // Mobile feedback filter styles
  mobileFeedbackFilterRow: { flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  exportButton: {
    marginLeft: 10,
    backgroundColor: AdminColors.blue700,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  exportButtonText: { fontFamily: FontFamily.bold, fontSize: 13, color: '#fff' },
  toggleContainer: {
    flexDirection: 'row',
    gap: 32,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.gray50,
  },
  toggleTab: {
    paddingVertical: 12,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
    marginBottom: -1,
  },
  toggleTabActive: {
    borderBottomColor: AdminColors.blue700,
  },
  toggleText: {
    fontFamily: FontFamily.bold,
    fontSize: 15,
    color: AdminColors.gray350,
  },
  toggleTextActive: {
    color: AdminColors.blue700,
  },
});

export default PerfectTruckDashboard;
